import React, { useState, useMemo, useCallback } from 'react';
import { IoChevronDown, IoInformationCircleOutline, IoTrashOutline } from 'react-icons/io5';
import { Watchlist, WatchlistSymbol } from '@benzinga/watchlist-manager';
import { Tooltip } from '@benzinga/core-ui';

import { Toggler, sortRows } from './index';

interface NotificationTableProps {
  watchlist: Watchlist;
  onRemoveNotificationTableRow: (watchlist: Watchlist, ticker: WatchlistSymbol) => Promise<void>;
  onUpdateWatchlistTicker: (updateParams: WatchlistSymbol) => Promise<void>;
}

const columns = [
  {
    title: 'News',
    id: 'sendRealtime',
    sortable: true,
    description: 'Real-time alerts sent via email & push notifications (download the Benzinga app)',
  },
  {
    title: 'Press',
    id: 'isPR',
    sortable: true,
    description: 'Company issued announcements',
  },
  {
    title: 'SEC',
    id: 'isSEC',
    sortable: true,
    description: 'Company issued legal filings',
  },
];

const SortCaret = ({ active, direction }: { active: boolean; direction?: 'asc' | 'desc' }) => (
  <IoChevronDown
    className={`size-3.5 shrink-0 transition-transform ${
      active ? 'opacity-100' : 'opacity-30'
    } ${active && direction === 'asc' ? 'rotate-180' : ''}`}
  />
);

const AlertLegend = () => (
  <div className="mb-3 hidden flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 sm:flex">
    {columns.map(column => (
      <Tooltip key={column.id} content={column.description} size="sm">
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-left hover:bg-slate-100 hover:text-slate-700"
        >
          <span className="font-semibold text-slate-600">{column.title}</span>
          <IoInformationCircleOutline size={14} className="text-slate-400" />
        </button>
      </Tooltip>
    ))}
  </div>
);

const NotificationTable: React.FC<NotificationTableProps> = ({
  watchlist,
  onRemoveNotificationTableRow,
  onUpdateWatchlistTicker,
}) => {
  const [sortType, setSortType] = useState<string | undefined>(undefined);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | undefined>(undefined);

  const sortedRows = useMemo(() => {
    if (!sortType || !sortDir) {
      return watchlist.symbols;
    }
    return sortRows(watchlist.symbols, sortType, sortDir);
  }, [watchlist.symbols, sortType, sortDir]);

  const setBool = useCallback(
    async (ticker: WatchlistSymbol, id: string, val: boolean) => {
      const updateParams = { ...ticker };
      updateParams[id] = val ? 1 : 0;
      await onUpdateWatchlistTicker(updateParams);
    },
    [onUpdateWatchlistTicker],
  );

  const removeNotificationTableRow = useCallback(
    async (currentWatchlist: Watchlist, ticker: WatchlistSymbol) => {
      await onRemoveNotificationTableRow(currentWatchlist, ticker);
    },
    [onRemoveNotificationTableRow],
  );

  const sortColumn = useCallback(
    (columnId: string) => {
      if (columnId !== sortType) {
        setSortType(columnId);
        setSortDir('asc');
        return;
      }

      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        setSortType(undefined);
        setSortDir(undefined);
      }
    },
    [sortType, sortDir],
  );

  if (sortedRows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center">
        <p className="text-sm font-semibold text-slate-800">No tickers in this watchlist</p>
        <p className="mt-1 text-sm text-slate-500">Add tickers to configure per-symbol alerts.</p>
      </div>
    );
  }

  return (
    <div>
      <AlertLegend />

      <div className="space-y-2 sm:hidden">
        {(sortedRows as WatchlistSymbol[]).map(row => (
          <div key={row.symbol} className="rounded-xl border border-slate-200 bg-white p-3.5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <a
                href={`/quote/${row.symbol}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-bold text-bzblue-800 hover:underline"
              >
                {row.symbol}
              </a>
              <button
                type="button"
                aria-label={`Remove ${row.symbol}`}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 text-xs font-semibold text-red-600 transition-colors hover:border-red-300 hover:bg-red-100 hover:text-red-700"
                onClick={() => removeNotificationTableRow(watchlist, row)}
              >
                <IoTrashOutline size={14} />
                Remove
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {columns.map(column => (
                <div
                  key={`${row.symbol}-${column.id}`}
                  className="flex flex-col items-center gap-2 rounded-lg bg-slate-50 px-2 py-2.5"
                >
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{column.title}</span>
                  <Toggler enabled={Boolean(row[column.id])} onChange={val => setBool(row, column.id, val)} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="hidden overflow-hidden rounded-xl border border-slate-200 sm:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="w-[36%] px-4 py-2.5 text-left">
                <button
                  type="button"
                  className={`inline-flex items-center gap-1 text-left text-[11px] font-bold uppercase tracking-wide ${
                    sortType === 'symbol' ? 'text-bzblue-800' : 'text-slate-500 hover:text-slate-800'
                  }`}
                  onClick={() => sortColumn('symbol')}
                >
                  Symbol
                  <SortCaret active={sortType === 'symbol'} direction={sortDir} />
                </button>
              </th>
              {columns.map(column => (
                <th key={column.id} className="px-3 py-2.5 text-center">
                  <button
                    type="button"
                    className={`inline-flex items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wide ${
                      sortType === column.id ? 'text-bzblue-800' : 'text-slate-500 hover:text-slate-800'
                    }`}
                    onClick={() => {
                      if (column.sortable) sortColumn(column.id);
                    }}
                  >
                    <span>{column.title}</span>
                    <SortCaret active={sortType === column.id} direction={sortDir} />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(sortedRows as WatchlistSymbol[]).map(row => (
              <tr key={row.symbol} className="group border-b border-slate-100 last:border-b-0 hover:bg-slate-50/80">
                <td className="px-4 py-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <a
                      href={`/quote/${row.symbol}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-bold text-bzblue-800 hover:underline"
                    >
                      {row.symbol}
                    </a>
                    <button
                      type="button"
                      aria-label={`Remove ${row.symbol}`}
                      className="rounded p-1 text-slate-300 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                      onClick={() => removeNotificationTableRow(watchlist, row)}
                    >
                      <IoTrashOutline size={15} />
                    </button>
                  </div>
                </td>
                {columns.map(column => (
                  <td key={`${row.symbol}-${column.id}`} className="px-3 py-3.5 text-center">
                    <div className="flex justify-center">
                      <Toggler enabled={Boolean(row[column.id])} onChange={val => setBool(row, column.id, val)} />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NotificationTable;
